import { types, actions, Action } from '.';
import { SagaIterator } from 'redux-saga';
import { put, takeLatest, call } from 'redux-saga/effects';
import * as providerApi from '../../../api/provider';
import { actions as appUIActions } from '../../uis/app';
import Provider, { ProviderInfoResponse, ProviderInfoResponseStatusCode, SubmitToVendorResponse } from 'src/models/provider';
import { actions as workflowActions } from '../../entities/workflow';
import { actions as experianQuestionActions } from '../../entities/experianQuestion';
import { increaseAttemptCounter, setAttemptCounter } from 'src/utils/workflow';
import Facility from 'src/models/facility';
import { rollbar } from 'src';

export default function* watchProviderData(): SagaIterator {
    yield takeLatest(types.ADD_PROVIDER_ASYNC, addProviderAsync);
    yield takeLatest(types.GET_PROVIDER_INFO_ASYNC, getProviderInfoAsync);
    yield takeLatest(types.GET_FACILITIES_BY_TENANT_NAME_ASYNC, getFacilitiesByTenantNameAsync);
    yield takeLatest(types.SUBMIT_TO_VENDOR_ASYNC, submitToVendorAsync);
    yield takeLatest(types.ADD_PROVIDER_INFORMATION_ASYNC, addProviderInformationAsync);
}

export function* addProviderAsync(action: Action): SagaIterator {
    try {
        yield put(workflowActions.checkRetry(false));

        const response: ProviderInfoResponse = yield call(providerApi.submitCredential, action.providerInfo);
        if (response != undefined) {
            yield put(workflowActions.storeWorkflowData(response.workflow));
        }

        if (response.success) {
            setAttemptCounter('0', true);
            yield put(actions.addProviderSucceeded());
            yield put(actions.storeProviderInfoPayment(action.providerInfo));
            yield put(workflowActions.storeWorkingRegistrationId(response.registrationId));
            yield put(workflowActions.storeExperianSessionId(response.workflow.sessionId));
            yield put(workflowActions.storeUserInfo(response.userInfo));
            yield put(experianQuestionActions.storeExperianQuestionData(response.questionSet));
            // yield put(workflowActions.storeWorkflowData(response.workflow));
            yield put(appUIActions.setMessageIndicator('Submit provider information successfully, go to the step 02', 'success'));
        } else {
            // With the latest workflow, by logging in with the user-name, the check email exists feature is unnecessary
            // if (!response.emailIsValid) {
            //     yield call(console.log, 'This email has been used.');
            //     yield put(actions.emailIsValid(false));
            //     yield put(appUIActions.setMessageIndicator(response.message, 'error'));
            // } else {
            //     yield put(workflowActions.checkRetry(true));
            //     increaseAttemptCounter();
            //     yield call(console.log, 'Cannot add provider!');
            // }
            yield put(actions.updateStatusCode(response.statusCode));
            yield put(workflowActions.checkRetry(true));
            increaseAttemptCounter(true);
            yield call(console.log, 'Add new provider information failed!');
            yield put(actions.addProviderFailed(response.message));
            // yield put(appUIActions.setMessageIndicator('Provider information something went wrong! Please try again.', 'error'));
            if (response.statusCode && (response.statusCode === ProviderInfoResponseStatusCode.Error_TerminatedUser || response.statusCode === ProviderInfoResponseStatusCode.Error_General)) {
                yield put(appUIActions.setMessageIndicator('Cannot add provider. ' + response.message, 'error'));
            }
        }
    } catch (e) {
        yield put(actions.updateStatusCode(undefined));
        yield put(workflowActions.checkRetry(true));
        increaseAttemptCounter(true);
        yield put(actions.addProviderFailed(''));
        yield call(console.log, 'Cannot add provider. ' + e);
        // yield put(appUIActions.setMessageIndicator('Cannot add provider!', 'error'));
        rollbar.error('Cannot add provider!');
    }
}

export function* getProviderInfoAsync(action: Action): SagaIterator {
    try {
        yield put(actions.actionServerDataProviderRequested());
        const provider: Provider = yield call(providerApi.getProviderById, action.registrationId);
        yield put(actions.getProviderInfoSucceeded(provider));
    } catch (e) {
        yield put(actions.actionServerDataProviderFailed());
        yield call(console.log, 'Cannot get provider info!');
        rollbar.error('Cannot get provider info!');
    }
}

export function* getFacilitiesByTenantNameAsync(action: Action): SagaIterator {
    try {
        const facilities: Facility[] = yield call(providerApi.getFacilitiesByTenantName, action.tenantName);
        yield put(actions.getFacilitiesByTenantNameSucceeded(facilities));
    } catch (e) {
        yield put(actions.getFacilitiesByTenantNameFailed());
        yield call(console.log, 'Cannot get facilities by tenant name!');
        rollbar.error('Cannot get facilities by tenant name!');
    }
}

export function* submitToVendorAsync(action: Action): SagaIterator {
    try {
        const response: SubmitToVendorResponse = yield call(providerApi.submitToVendor, action.registrationId);

        if (response.success) {
            yield put(actions.submitToVendorSucceeded());
        } else {
            yield put(actions.submitToVendorFailed());
            yield put(appUIActions.setMessageIndicator('Submit to Vendor failed! ' + response.message, 'error'));
            yield call(console.log, 'Cannot submit to Vendor! ' + response.message);
        }
    } catch (e) {
        console.log(e);
        yield put(actions.submitToVendorFailed());
        yield put(appUIActions.setMessageIndicator('Submit to Vendor failed! General error', 'error'));
        yield call(console.log, 'Cannot submit to Vendor!', e);
        rollbar.error('Cannot submit to Vendor!');
    }
}

export function* addProviderInformationAsync(action: Action): SagaIterator {
    try {
        yield put(workflowActions.checkRetry(false));

        const response: ProviderInfoResponse = yield call(providerApi.submitProviderInformation, action.providerInformation);

        if (response != undefined) {
            yield put(workflowActions.storeWorkflowData(response.workflow));
        }

        if (response.success) {
            setAttemptCounter('0', true);
            yield put(actions.addProviderInformationSucceeded());
            yield put(actions.storeProviderInformationPayment(action.providerInformation));
            yield put(workflowActions.storeWorkingRegistrationId(response.registrationId));
            yield put(workflowActions.storeExperianSessionId(response.workflow.sessionId));
            yield put(workflowActions.storeUserInfo(response.userInfo));
            yield put(experianQuestionActions.storeExperianQuestionData(response.questionSet));
            yield put(appUIActions.setMessageIndicator('Submit provider information successfully, go to the step 02', 'success'));
        } else {
            yield put(actions.updateStatusCode(response.statusCode));
            yield put(workflowActions.checkRetry(true));
            increaseAttemptCounter(true);
            yield call(console.log, 'Add new provider information failed!');
            yield put(actions.addProviderInformationFailed(response.message));
            if (response.statusCode && (response.statusCode === ProviderInfoResponseStatusCode.Error_TerminatedUser || response.statusCode === ProviderInfoResponseStatusCode.Error_General)) {
                yield put(appUIActions.setMessageIndicator('Cannot add provider. ' + response.message, 'error'));
            }
        }
    } catch (e) {
        yield put(actions.updateStatusCode(undefined));
        yield put(workflowActions.checkRetry(true));
        increaseAttemptCounter(true);
        yield put(actions.addProviderInformationFailed(''));
        yield call(console.log, 'Cannot add provider. ' + e);
        rollbar.error('Cannot add provider!');
    }
}


// WEBPACK FOOTER //
// ./src/modules/entities/provider/saga.ts